import { useEffect, useState } from 'react'
import type { Expense } from '../../lib/db'
import { saveExpense, getFuelPrice } from '../../lib/sync'
import CategoryPicker from '../record/CategoryPicker'
import AmountInput from '../record/AmountInput'
import OdometerInput from '../record/OdometerInput'
import FuelFields from '../record/FuelFields'
import DescriptionInput from '../record/DescriptionInput'
import type { Category, FuelGrade } from '../record/constants'
import { evalAmount } from '../../lib/eval-amount'

interface Props {
  expense: Expense
  onClose: () => void
  onSaved: (updated: Expense) => void
}

type Status = 'idle' | 'saving' | 'saved' | 'error'

export default function EditModal({ expense, onClose, onSaved }: Props) {
  const [category, setCategory] = useState<Category>(expense.category as Category)
  const [amount, setAmount] = useState(String(expense.amount))
  const [odometer, setOdometer] = useState(expense.odometer_km != null ? String(expense.odometer_km) : '')
  const [description, setDescription] = useState(expense.description ?? '')
  const [note, setNote] = useState(expense.note ?? '')
  const [date, setDate] = useState(expense.date)
  const [fuelGrade, setFuelGrade] = useState<FuelGrade>((expense.fuel_grade as FuelGrade) ?? '95')
  const [liters, setLiters] = useState(expense.liters != null ? String(expense.liters) : '')
  const [isFullTank, setIsFullTank] = useState(expense.is_full_tank ?? true)
  const [fuelPrice, setFuelPrice] = useState<number | null>(null)
  const [status, setStatus] = useState<Status>('idle')

  useEffect(() => {
    getFuelPrice(date, fuelGrade).then(setFuelPrice)
  }, [date, fuelGrade])

  function handleCategoryChange(cat: Category) {
    setCategory(cat)
    if (cat !== '油錢') {
      setFuelGrade('95')
      setLiters('')
      setIsFullTank(true)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const numAmount = evalAmount(amount)
    if (!numAmount) return
    setStatus('saving')

    const now = new Date().toISOString()
    const isFuel = category === '油錢'

    try {
      const updated: Expense = {
        ...expense,
        date,
        category,
        description: description || null,
        amount: numAmount,
        odometer_km: odometer ? parseInt(odometer, 10) : null,
        note: note || null,
        fuel_grade: isFuel ? fuelGrade : null,
        liters: isFuel && liters ? parseFloat(liters) : null,
        is_full_tank: isFuel ? isFullTank : null,
        updated_at: now,
      }
      await saveExpense(updated)
      setStatus('saved')
      setTimeout(() => {
        onSaved(updated)
        onClose()
      }, 800)
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="modal-sheet">
        <div className="modal-header">
          <span className="modal-title">編輯紀錄</span>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>

        <form className="record-page modal-body" onSubmit={handleSubmit} noValidate>
          <CategoryPicker value={category} onChange={handleCategoryChange} />

          <AmountInput value={amount} onChange={setAmount} />

          <OdometerInput
            value={odometer}
            lastOdometer={0}
            onChange={setOdometer}
          />

          {category === '油錢' && (
            <FuelFields
              grade={fuelGrade}
              liters={liters}
              isFullTank={isFullTank}
              amount={amount}
              fuelPrice={fuelPrice}
              onGrade={setFuelGrade}
              onLiters={setLiters}
              onFullTank={setIsFullTank}
            />
          )}

          <DescriptionInput
            category={category}
            value={description}
            onChange={setDescription}
          />

          <div className="field">
            <label htmlFor="edit-note">備註（選填）</label>
            <input
              id="edit-note"
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="自由輸入"
              autoComplete="off"
            />
          </div>

          <div className="field">
            <label htmlFor="edit-date">日期</label>
            <input
              id="edit-date"
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
            />
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>取消</button>
            <button
              type="submit"
              className="submit-btn"
              disabled={!evalAmount(amount) || status === 'saving'}
            >
              {status === 'saving' ? '儲存中…' : status === 'saved' ? '已儲存' : '儲存'}
            </button>
          </div>

          {status === 'error' && <p className="status-msg error">儲存失敗，請重試</p>}
        </form>
      </div>
    </div>
  )
}
